import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { isEqual } from 'lodash';
import { Layout, useForm, Heading, Text, Button, extractErrorMessage } from '@netapp/bxp-design-system-react';
import { EmailServerConfigForm } from 'modules/console/components/email-configuration/EmailServerConfiguration';
import { notificationSettings as i18nNamespace } from '../../../i18n/namespace';
import { ReactComponent as IllustrationImage } from 'modules/ncl-onboarding/Organisation.svg';
import styles from './NclSmtpSetup.module.scss';
import { apiClient } from 'utils/clients';
import TestEmailConfigDialog from 'modules/console/components/email-configuration/TestEmailConfigDialog';
import { PREFERENCES_KEYS } from '../middlewares/userPreferencesMiddleware';
import { getUploadedFileSizeBytes, SMTP_CERT_MAX_FILE_SIZE_BYTES, SMTP_CERT_MAX_FILE_SIZE_KB } from 'utils/notificationsUtils';

const SECURITY_CONFIGURATION_OPTIONS = {
    STARTTLS: 'starttls',
    SMTPS: 'smtps'
};

const NclSmtpSetup = () => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const { selectedOrgId, rules } = useSelector(state => state.tenancy);

    const connectionSecurityOptions = [
        { label: t('STARTTLS'), value: SECURITY_CONFIGURATION_OPTIONS.STARTTLS },
        { label: t('SMTPS'), value: SECURITY_CONFIGURATION_OPTIONS.SMTPS }
    ];

    const initialState = {
        id: '',
        host: '',
        connectionSecurity: connectionSecurityOptions[0],
        port: 25,
        username: '',
        password: '',
        fromName: '',
        fromAddress: '',
        startTLS: true,
        ssl: false,
        tlsCertificate: null
    };

    const validateSchema = yup.object().shape({
        host: yup.string().required(t('smtp_hostname_required')),
        port: yup.number()
            .min(1, t('smtp_port_must_be_greater_than_zero'))
            .max(65535, t('smtp_port_must_be_less_than_65536'))
            .typeError(t('smtp_port_must_be_number'))
            .required(t('smtp_port_required')),
        username: yup.string(),
        password: yup.string(),
        fromName: yup.string().required(t('smtp_from_name_required')),
        fromAddress: yup.string().email(t('smtp_from_address_invalid')).required(t('smtp_from_address_required')),
        tlsCertificate: yup
                    .mixed()
                    .nullable()
                    .test(
                        'smtp-certificate-max-size',
                        t('certificate_max_size', {
                            maxSize: `${SMTP_CERT_MAX_FILE_SIZE_KB} KB`,
                        }),
                        value => {
                            if (!value) {
                                return true;
                            }
                            return getUploadedFileSizeBytes(value) <= SMTP_CERT_MAX_FILE_SIZE_BYTES;
                        }
                    )
    }).test('username-password', null, function (values) {
        const { username, password } = values;
        if (username && !password) return this.createError({ path: 'password', message: t('smtp_password_required') });
        if (!username && password) return this.createError({ path: 'username', message: t('smtp_username_required') });
        return true;
    });

    const form = useForm(initialState, validateSchema);

    const [isFirstTimeRegister, setIsFirstTimeRegister] = useState(true);
    const [isEmailConfigSaveInProgress, setIsEmailConfigSaveInProgress] = useState(false);
    const [isTestEmailConfigOpen, setIsTestEmailConfigOpen] = useState(false);
    const [isTestEmailSuccess, setIsTestEmailSuccess] = useState(false);

    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    useEffect(() => {
        const checkIsSaveDisabled = () => {
            if (isFirstTimeRegister) {
                return !form.isValid;
            }
            const hasChanges = !isEqual(form.formState, initialState);
            return !(hasChanges && form.isValid);
        };
        setIsSaveDisabled(checkIsSaveDisabled());
    }, [form.formState, form.isValid, isFirstTimeRegister]);

    const onConnectionSecurityChange = ({ value }) => {
        if (value === 'starttls') {
            form.handleFormChange(connectionSecurityOptions[0], { name: 'connectionSecurity' });
            form.handleFormChange(25, { name: 'port' });
            form.handleFormChange(true, { name: 'startTLS' });
            form.handleFormChange(false, { name: 'ssl' });
        } else if (value === 'smtps') {
            form.handleFormChange(connectionSecurityOptions[1], { name: 'connectionSecurity' });
            form.handleFormChange(465, { name: 'port' });
            form.handleFormChange(false, { name: 'startTLS' });
            form.handleFormChange(true, { name: 'ssl' });
        }
    };

    const handleTestEmailConfig = async () => {
        if (!form.isValid) return;
        setIsTestEmailConfigOpen(true);
    };

    const getKeycloakResp = (payload) => ({
        host: payload.host,
        port: `${payload.port}`,
        from: payload.fromAddress,
        fromDisplayName: payload.fromName,
        ssl: `${payload.ssl}`,
        starttls: `${payload.startTLS}`,
        authentication: (payload.username && payload.password) ? 'true' : 'false',
        ...((payload.username && payload.username !== '') && { authenticationUser: payload.username }),
        ...((payload.password && payload.password !== '') && { authenticationPassword: payload.password })
    });

    const handleSaveEmailConfig = async () => {
        if (!form.isValid) return; 
        setIsEmailConfigSaveInProgress(true);

        const payload = { ...form.formState };
        payload.tlsCertificate = payload.tlsCertificate ? payload.tlsCertificate.contents : '';
        payload.port = Number(payload.port);
        delete payload.connectionSecurity;
        delete payload.id;
        if (payload.username === '' || payload.password === '') {
            delete payload.username;
            delete payload.password;
        }
        try {
            const response = await apiClient.post(
                `${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/email-config`,
                payload
            );
            if (response.data) {
                try {
                    await apiClient.post(`${process.env.REACT_APP_OCCM_ENDPOINT}api/v1/smtp`, getKeycloakResp(payload));
                } catch (error) {
                    dispatch({
                        type: 'NOTIFICATIONS:WARNING',
                        payload: {
                            message: t('failed_to_post_email_config'),
                            key: 'POST-EMAIL-CONFIG-KEYCLOAK',
                            showGlobal: true
                        }
                    });
                }
                dispatch({
                    type: 'NOTIFICATIONS:SUCCESS',
                    payload: {
                        message: t('email_config_added_successfully'),
                        key: 'POST-EMAIL-CONFIG',
                        showGlobal: true
                    }
                });
                setIsFirstTimeRegister(false);
                setIsEmailConfigSaveInProgress(false);
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        } catch (error) {
            dispatch({
                type: 'NOTIFICATIONS:ERROR',
                payload: {
                    message: t('failed_to_post_email_config'),
                    exception: error,
                    moreInfo: extractErrorMessage(error, 'detail'),
                    key: 'POST-EMAIL-CONFIG',
                    showGlobal: true
                }
            });
            setIsEmailConfigSaveInProgress(false);
        }
    };

    const onSaveClick = (event) => {
        form.handleFormSubmit(handleSaveEmailConfig)(event);
    };

    const handleSkip = () => {
        dispatch({
            type: "PREFERENCES:SAVE_OR_UPDATE_REMOTE",
            payload: {
                id: null,
                name: PREFERENCES_KEYS.SMTP_SETUP_SKIPPED_PREFERENCE_KEY,
                data: true,
            }
        }).finally(
            () => {
                window.location.reload()
            }
        )
    };

    return (
        <Layout.Page>
            <Layout.Content>
                <Layout.Container className={styles.container}>
                    <Layout.Grid variant="grid" className={styles['email-server-layout']}>
                        <IllustrationImage style={{ zIndex: 50 }} />
                        <div className={styles["form-container"]}>
                            <div className={styles['email-config-card-header']}>
                                <Heading style={{ marginBottom: '16px' }} bold level={3}>{t('email_server_setup_title')}</Heading>
                                <Text level="14">{t('email_server_setup_description')}</Text>
                            </div>
                            <EmailServerConfigForm
                                form={form}
                                t={t}
                                connectionSecurityOptions={connectionSecurityOptions}
                                onConnectionSecurityChange={onConnectionSecurityChange}
                                isFirstTimeRegister={isFirstTimeRegister}
                                rules={rules}
                                isEmailConfigSaveInProgress={isEmailConfigSaveInProgress}
                                handleTestEmailConfig={handleTestEmailConfig}
                                isSMTPOnboardingFlow={true}
                            />
                            {isTestEmailConfigOpen && <TestEmailConfigDialog emailServerConfigDetailsForm={form} setIsTestEmailConfigOpen={setIsTestEmailConfigOpen} onSuccess={() => setIsTestEmailSuccess(true)} />}
                            <div className={styles['buttons-group']}>
                                <Button
                                    className={styles.width152}
                                    isSubmitting={isEmailConfigSaveInProgress}
                                    disabled={isSaveDisabled || !isTestEmailSuccess}
                                    onClick={onSaveClick}>
                                    {t('save')}
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={handleSkip}>
                                    {t('skip')}
                                </Button>
                            </div>
                        </div>
                    </Layout.Grid>
                </Layout.Container>
            </Layout.Content>
        </Layout.Page>
    );
};

export default NclSmtpSetup;
